import React, { useState, useMemo } from 'react';
import type { UserAnswers } from '../types';

interface GlassWineQuestionnaireProps {
  onSubmit: (answers: UserAnswers) => void;
  onBack: () => void;
  occasions: string[];
  tastes: string[];
  origins: string[];
}

const GlassWineQuestionnaire: React.FC<GlassWineQuestionnaireProps> = ({
  onSubmit,
  onBack,
  occasions,
  tastes,
  origins
}) => {
  const [answers, setAnswers] = useState<UserAnswers>({
    occasion: '指定なし',
    food: '',
    color: '指定なし',
    taste: '指定なし',
    origin: '指定なし'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(answers);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAnswers(prev => ({ ...prev, [name]: value }));
  };

  const formOptions = useMemo(() => {
    return {
      occasion: occasions.length > 0 ? occasions : ['指定なし'],
      color: ['指定なし', '赤', '白', 'ロゼ', 'スパークリング'],
      taste: tastes.length > 0 ? tastes : ['指定なし'],
      origin: ['指定なし', ...origins],
    };
  }, [occasions, tastes, origins]);

  return (
    <div className="w-full max-w-2xl bg-white/50 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-2xl shadow-xl border border-gray-200/50 mx-2">
      <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-2">あなたの好みをお聞かせください</h2>
      <p className="text-center text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">最適なグラスワインをご提案します。</p>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Occasion */}
        <QuestionSelect name="occasion" label="1. どんなシーンで飲みますか？" value={answers.occasion} options={formOptions.occasion} onChange={handleChange} />

        {/* Food Pairing */}
        <div>
          <label htmlFor="food" className="block text-base sm:text-lg font-semibold text-gray-700 mb-2">2. どんなお料理と合わせますか？</label>
          <input
            type="text"
            id="food"
            name="food"
            value={answers.food}
            onChange={handleChange}
            placeholder="例：ステーキ、クリームパスタ、お寿司など"
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition text-sm sm:text-base"
          />
        </div>

        {/* Color */}
        <div>
          <label className="block text-base sm:text-lg font-semibold text-gray-700 mb-2">3. ワインの色は？</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {formOptions.color.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleChange({ target: { name: 'color', value: color } } as any)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  answers.color === color
                    ? 'bg-red-800 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:border-red-300'
                }`}
              >
                {color}
              </button>
            ))}
          </div>
        </div>

        {/* Taste */}
        <QuestionSelect name="taste" label="4. どんな味わいがお好みですか？" value={answers.taste} options={formOptions.taste} onChange={handleChange} />

        {/* Origin */}
        <QuestionSelect name="origin" label="5. 産地の希望はありますか？" value={answers.origin} options={formOptions.origin} onChange={handleChange} />

        <div className="pt-4">
          <button
            type="submit"
            className="w-full bg-red-800 text-white font-bold py-3 px-6 sm:px-8 rounded-lg shadow-lg hover:bg-red-900 transition-colors duration-300 transform hover:scale-105 text-sm sm:text-base"
          >
            グラスワインを選ぶ
          </button>
        </div>
        
        {/* 戻るボタン */}
        <div className="pt-4">
          <button
            type="button"
            onClick={onBack}
            className="w-full flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900 rounded-lg transition-colors duration-200 text-sm sm:text-base border border-gray-300"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            前のページに戻る
          </button>
        </div>
      </form>
    </div>
  );
};

// QuestionSelect コンポーネント
interface QuestionSelectProps {
  name: string;
  label: string;
  value: string;
  options: string[];
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const QuestionSelect: React.FC<QuestionSelectProps> = ({ name, label, value, options, onChange }) => (
  <div>
    <label htmlFor={name} className="block text-base sm:text-lg font-semibold text-gray-700 mb-2">{label}</label>
    <div className="relative">
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full appearance-none bg-white px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition text-sm sm:text-base"
      >
        {options.map((option, idx) => (
          <option key={`${option}__${idx}`} value={option}>{option}</option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
      </div>
    </div>
  </div>
);

export default GlassWineQuestionnaire;
